"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Q = void 0;
const Length_1 = require("./Length");
const Quantity_1 = require("./Quantity");
const Weight_1 = require("./Weight");
const Acceleration_1 = require("./Acceleration");
const Velocity_1 = require("./Velocity");
const WeightPerLength_1 = require("./WeightPerLength");
const Temperature_1 = require("./Temperature");
const AnglePerLength_1 = require("./AnglePerLength");
const Angle_1 = require("./Angle");
const Area_1 = require("./Area");
const BendingFactor_1 = require("./BendingFactor");
const Force_1 = require("./Force");
const Frequency_1 = require("./Frequency");
const Volume_1 = require("./Volume");
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
function isAccelerationUnit(unit) {
    return unit in Acceleration_1.AccelerationUnits;
}
function isAngleUnit(unit) {
    return unit in Angle_1.AngleUnits;
}
function isAnglePerLengthUnit(unit) {
    return unit in AnglePerLength_1.AnglePerLengthUnits;
}
function isAreaUnit(unit) {
    return unit in Area_1.AreaUnits;
}
function isBendingFactorUnit(unit) {
    return Boolean(Object.entries(BendingFactor_1.BendingFactorUnits).find(([, value]) => value === unit));
}
function isForceUnit(unit) {
    return unit in Force_1.ForceUnits;
}
function isFrequencyUnit(unit) {
    return unit in Frequency_1.FrequencyUnits;
}
function isLengthUnit(unit) {
    return unit in Length_1.LengthUnits;
}
function isTemperatureUnit(unit) {
    return Boolean(Object.entries(Temperature_1.TemperatureUnits).find(([, value]) => value === unit));
}
function isVelocityUnit(unit) {
    return unit in Velocity_1.VelocityUnits;
}
function isVolumeUnit(unit) {
    return unit in Volume_1.VolumeUnits;
}
function isWeightUnit(unit) {
    return unit in Weight_1.WeightUnits;
}
function isWeightPerLengthUnit(unit) {
    return unit in WeightPerLength_1.WeightPerLengthUnits;
}
function toQuantityJson(args) {
    if (!args) {
        return undefined;
    }
    if (args.length === 1 && (0, Quantity_1.isQuantityJson)(args[0])) {
        return args[0];
    }
    if ((0, Quantity_1.isQuantityArray)(args)) {
        return { Value: args[0], Unit: args[1] };
    }
    return undefined;
}
// eslint-disable-next-line
function Q(...args) {
    const q = toQuantityJson(args);
    if (!q) {
        throw new Error('Q() no valid quantity given');
    }
    if (isAccelerationUnit(q.Unit)) {
        return Acceleration_1.Acceleration.fromJson(q);
    }
    if (isAngleUnit(q.Unit)) {
        return Angle_1.Angle.fromJson(q);
    }
    if (isAnglePerLengthUnit(q.Unit)) {
        return AnglePerLength_1.AnglePerLength.fromJson(q);
    }
    if (isAreaUnit(q.Unit)) {
        return Area_1.Area.fromJson(q);
    }
    if (isBendingFactorUnit(q.Unit)) {
        return BendingFactor_1.BendingFactor.fromJson(q);
    }
    if (isForceUnit(q.Unit)) {
        return Force_1.Force.fromJson(q);
    }
    if (isFrequencyUnit(q.Unit)) {
        return Frequency_1.Frequency.fromJson(q);
    }
    if (isLengthUnit(q.Unit)) {
        return Length_1.Length.fromJson(q);
    }
    if (isTemperatureUnit(q.Unit)) {
        return Temperature_1.Temperature.fromJson(q);
    }
    if (isVelocityUnit(q.Unit)) {
        return Velocity_1.Velocity.fromJson(q);
    }
    if (isVolumeUnit(q.Unit)) {
        return Volume_1.Volume.fromJson(q);
    }
    if (isWeightUnit(q.Unit)) {
        return Weight_1.Weight.fromJson(q);
    }
    if (isWeightPerLengthUnit(q.Unit)) {
        return WeightPerLength_1.WeightPerLength.fromJson(q);
    }
    throw new Error(`Q: unknown quantity ${q.Unit}`);
}
exports.Q = Q;
// eslint-disable-next-line
function optional(...args) {
    const q = toQuantityJson(args);
    if (q) {
        return Q(q);
    }
    return undefined;
}
Q.optional = optional;
// shortcuts
Q.Acceleration = Acceleration_1.Acceleration;
Q.Angle = Angle_1.Angle;
Q.AnglePerLength = AnglePerLength_1.AnglePerLength;
Q.Area = Area_1.Area;
Q.BendingFactor = BendingFactor_1.BendingFactor;
Q.Force = Force_1.Force;
Q.Frequency = Frequency_1.Frequency;
Q.Length = Length_1.Length;
Q.Temperature = Temperature_1.Temperature;
Q.Velocity = Velocity_1.Velocity;
Q.Volume = Volume_1.Volume;
Q.Weight = Weight_1.Weight;
Q.WeightPerLength = WeightPerLength_1.WeightPerLength;
/**
 * Retrieves the sum of all Quantities or 0 in case of empty or undefined
 */
Q.sum = (qs, unit) => {
    let sum = 0;
    for (const q of qs) {
        if (!q || !Q(q).hasDefinedValue())
            continue; // for untyped consumers
        sum = sum + Q(q).getValue(unit);
    }
    return Q(sum, unit).quantity;
};
// eslint-disable-next-line
Q.areEqual = (a, b) => {
    if ((!a && b) || (a && !b))
        return false;
    if (!a && !b)
        return true;
    return (a === null || a === void 0 ? void 0 : a.getValue('default')) === (b === null || b === void 0 ? void 0 : b.getValue('default'));
};
/**
 * Retrieves the lowest `Quantity`.
 * It will handle partial undefined values, but requires at least one defined.
 */
Q.getLowest = (qs) => {
    let target = qs[0];
    qs.slice(1).forEach((q) => {
        if (q != null) {
            if (target == null) {
                target = q;
            }
            else if (q.getValue('default') < target.getValue('default')) {
                target = q;
            }
        }
    });
    if (target == null) {
        throw new Error('Q.getBiggest() => undefined ');
    }
    return target;
};
/**
 * Retrieves the biggest `Quantity`.
 * It will handle partial undefined values, but requires at least one defined.
 */
Q.getBiggest = (qs) => {
    let target = qs[0];
    qs.slice(1).forEach((q) => {
        if (q != null) {
            if (target == null) {
                target = q;
            }
            else if (q.getValue('default') > target.getValue('default')) {
                target = q;
            }
        }
    });
    if (target == null) {
        throw new Error('Q.getBiggest() => undefined ');
    }
    return target;
};
/**
 * Retrieves the lowest `QuantityJson`.
 * It will handle partial undefined values, but requires at least one defined.
 */
Q.getLowestJson = (qs) => {
    const definedQs = qs.filter((q) => q != null);
    let target = definedQs[0];
    definedQs.slice(1).forEach((q) => {
        if (q != null) {
            if (target == null) {
                target = q;
            }
            else if (Q(q).getValue('default') < Q(target).getValue('default')) {
                target = q;
            }
        }
    });
    if (target == null) {
        throw new Error('Q.getLowestJson() => undefined ');
    }
    return target;
};
/**
 * Retrieves the highest `QuantityJson`.
 * It will handle partial undefined values, but requires at least one defined.
 */
Q.getBiggestJson = (qs) => {
    const definedQs = qs.filter((q) => q != null);
    let target = definedQs[0];
    definedQs.slice(1).forEach((q) => {
        if (q != null) {
            if (target == null) {
                target = q;
            }
            else if (Q(q).getValue('default') > Q(target).getValue('default')) {
                target = q;
            }
        }
    });
    if (target == null) {
        throw new Error('Q.getBiggestJson() => undefined ');
    }
    return target;
};
/**
 * Returns the lower of the two given quantities whereas `undefined` is not considered to be
 * lower. I.e. if `a` is `undefined`, `b` will be returned. If both are `undefined`, `undefined`
 * will be returned.
 */
// eslint-disable-next-line
Q.getLower = (a, b) => {
    if (a != null && b != null) {
        if (a.getValue('default') < b.getValue('default')) {
            return a;
        }
        else {
            return b;
        }
    }
    if (a != null) {
        return a;
    }
    else {
        return b;
    }
};
/**
 * Returns the higher of the two given Quantities whereas `undefined` is not considered to be
 * lower. Acts like `getLower`.
 */
// eslint-disable-next-line
Q.getHigher = (a, b) => {
    if (a != null && b != null) {
        if (a.getValue('default') > b.getValue('default')) {
            return a;
        }
        else {
            return b;
        }
    }
    if (a != null) {
        return a;
    }
    else {
        return b;
    }
};
Q.getUnitText = (unit) => {
    if (isAccelerationUnit(unit)) {
        return Acceleration_1.Acceleration.getUnitText(Acceleration_1.AccelerationUnits[unit]);
    }
    if (isAngleUnit(unit)) {
        return Angle_1.Angle.getUnitText(Angle_1.AngleUnits[unit]);
    }
    if (isAnglePerLengthUnit(unit)) {
        return AnglePerLength_1.AnglePerLength.getUnitText(AnglePerLength_1.AnglePerLengthUnits[unit]);
    }
    if (isAreaUnit(unit)) {
        return Area_1.Area.getUnitText(Area_1.AreaUnits[unit]);
    }
    if (isBendingFactorUnit(unit)) {
        const obj = Object.entries(BendingFactor_1.BendingFactorUnits).find(([, value]) => value === unit);
        if (obj) {
            const enumValue = obj[1];
            return BendingFactor_1.BendingFactor.getUnitText(enumValue);
        }
        throw new Error(`Q.getUnitText() Cannot map BendingFactorUnit to BendingFactorUnits`);
    }
    if (isForceUnit(unit)) {
        return Force_1.Force.getUnitText(Force_1.ForceUnits[unit]);
    }
    if (isFrequencyUnit(unit)) {
        return Frequency_1.Frequency.getUnitText(Frequency_1.FrequencyUnits[unit]);
    }
    if (isLengthUnit(unit)) {
        return Length_1.Length.getUnitText(Length_1.LengthUnits[unit]);
    }
    if (isTemperatureUnit(unit)) {
        const obj = Object.entries(Temperature_1.TemperatureUnits).find(([, value]) => value === unit);
        if (obj) {
            const enumValue = obj[1];
            return Temperature_1.Temperature.getUnitText(enumValue);
        }
        throw new Error(`Q.getUnitText() Cannot map BendingFactorUnit to BendingFactorUnits`);
    }
    if (isVelocityUnit(unit)) {
        return Velocity_1.Velocity.getUnitText(Velocity_1.VelocityUnits[unit]);
    }
    if (isVolumeUnit(unit)) {
        return Volume_1.Volume.getUnitText(Volume_1.VolumeUnits[unit]);
    }
    if (isWeightUnit(unit)) {
        return Weight_1.Weight.getUnitText(Weight_1.WeightUnits[unit]);
    }
    if (isWeightPerLengthUnit(unit)) {
        return WeightPerLength_1.WeightPerLength.getUnitText(WeightPerLength_1.WeightPerLengthUnits[unit]);
    }
    throw new Error(`Q.getUnitText: unknown unit ${unit}`);
};
/**
 * Create a QuantityJson object for a given value/unit pair.
 *
 * It uses NumberUtils to allow only real useful and clear number representations.
 */
// eslint-disable-next-line
Q.createJson = (value, unit, defaultValue) => {
    let num = echain_shared_ts_utils_1.NumberUtils.toNumber(value);
    if (num == null)
        num = defaultValue;
    if (echain_shared_ts_utils_1.NumberUtils.isDefinedNumber(num)) {
        return { Value: num, Unit: unit };
    }
    return undefined;
};
//# sourceMappingURL=Q.js.map